import { jwtDecode } from 'jwt-decode';

interface DecodedAccessToken {
  exp?: number;
  claims?: string[];
  role?: unknown;
  [key: string]: unknown;
}

/**
 * The single place that knows the JWT `exp` claim is in seconds.
 *
 * Returns the expiry as epoch milliseconds, or `null` when the token is
 * missing, unparseable, or carries no `exp`.
 */
export function readExpMs(token: string | null | undefined): number | null {
  if (!token) return null;
  try {
    const decoded: DecodedAccessToken = jwtDecode(token);
    if (typeof decoded.exp !== 'number' || !Number.isFinite(decoded.exp)) {
      return null;
    }
    return decoded.exp * 1000;
  } catch {
    return null;
  }
}

/**
 * True when the token is expired, unreadable, or expires within `skewMs`.
 * An unreadable token counts as expired — we can never trust it.
 */
export function isExpired(
  token: string | null | undefined,
  skewMs = 0,
): boolean {
  const exp = readExpMs(token);
  if (exp === null) return true;
  return Date.now() >= exp - skewMs;
}
